import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { notification } from 'antd';
import { useHistory } from 'react-router-dom';
import { Window } from '../../utils/window';
import UserIcon from '../../assets/login/UserIcon';
import MailIcon from '../../assets/login/MailIcon';
import PasswordIcon from '../../assets/login/PasswordIcon';
import LoginInput from '../LoginInput/LoginInput';
import { auth0Client } from '../../auth0/auth0';
import loginValidations from '../../hooks/validations/login/loginValidations';
import { t } from '../../i18n/i18n';
import './LoginForm.scss';
import { setLoginLoading } from '../../redux/actions/authActions';
import Modal from '../Modal/Modal';
import Spinner from '../spinner/Spinner';
import useTermsAndConditions from '../../hooks/useTermsAndConditions';
import PDFViewer from '../PDFViewer/PDFViewer';
import { FORGOTPASSWORD, REGISTER, SIGNIN } from '../../constants/routes';
import { getGoogleValidAccount } from '../../API/services/alkymersService';
import DebouncedButton from '../Button/Button';
import { handleAuthResult } from '../../auth0/customOTPLogin';
import { loginWithOTPCode, sendCodeToLogin } from '../../API/services/authService';
import HorizontalLoader from '../HorizontalLoader/HorizontalLoader';
import OTPInputCodePassword from '../OTPInputCodePassword/OTPInputCodePassword';
import useRoles from '../../hooks/useRoles';
import useSidebar from '../../hooks/useSidebar';
import useTranslations from '../../hooks/useTranslations';
import { PARTNERS } from '../../constants/enums';

const LoginForm = ({
  haveAccount,
  recoverPassword,
  setHaveAccount,
  loginWithOTP,
  setLoginWithOTP,
  sendCode,
  setSendCode,
}) => {
  const history = useHistory();
  const [loginErrors, setLoginErrors] = useState({});
  const dispatch = useDispatch();
  const { roles } = useRoles();
  const { setCollapsed } = useSidebar();
  const { key: currentLanguage } = useTranslations();
  // Priorizar localStorage para el idioma del OTP
  const otpLanguage = localStorage.getItem('language') || currentLanguage || 'es';
  const [user, setUser] = useState({
    email: '',
    password: '',
    name: '',
    lastName: '',
  });
  const [error, setError] = useState('');
  const { validationsFn } = loginValidations(t);
  const [isDisabled, setIsDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isOTPDisabled, setIsOTPDisabled] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const initialCodeOTP = Array.from({ length: 4 }, () => '');
  const [codeOTP, setCodeOTP] = useState(initialCodeOTP);

  useEffect(() => {
    setError('');
    setLoginErrors({});
    setUser({ email: '', password: '', name: '', lastName: '' });
  }, [haveAccount, recoverPassword]);

  const onChange = (e) => {
    setError('');
    if (
      e.target.name === 'email' &&
      e.target.value.includes('@') &&
      e.target.value.split('.')?.length > 1 &&
      haveAccount
    ) {
      delete loginErrors[e.target.name];
      setUser({ ...user, [e.target.name]: e.target.value });
    } else {
      const errors = validationsFn(e.target.name, e.target.value, haveAccount);
      if (errors?.length > 0) {
        setLoginErrors({
          ...loginErrors,
          [e.target.name]: errors,
        });
      } else {
        delete loginErrors[e.target.name];
      }
      setUser({ ...user, [e.target.name]: e.target.value });
    }
  };

  const loginPassword = async () => {
    if (Window()) {
      setLoading(true);
      dispatch(setLoginLoading(true));
      auth0Client().login(
        {
          realm: 'Username-Password-Authentication',
          username: user.email,
          password: user.password,
        },
        (e) => {
          setLoading(false);
          dispatch(setLoginLoading(false));
          setError(e?.description || '');
        }
      );
    }
  };

  const startOTPCountdown = () => {
    setIsOTPDisabled(true);
    setOtpCountdown(120);

    const interval = setInterval(() => {
      setOtpCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsOTPDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendEmailLoginCode = async () => {
    const body = {
      email: user.email,
      platform: PARTNERS,
      language: otpLanguage,
    };

    setLoading(true);
    if (Window()) {
      try {
        await sendCodeToLogin(body);
        notification.open({
          message: t('RECOVER_PASSWORD_SUCCESS'),
          type: 'success',
        });
        setSendCode(true);
        startOTPCountdown();
      } catch (err) {
        notification.open({
          message: t(err?.response?.data?.message ?? 'ERROR_SENDING_OTP_CODE'),
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const loginWithCode = async () => {
    const body = {
      email: user.email,
      platform: PARTNERS,
      otp: codeOTP.join(''),
      otpWorkFlow: 'OTP_LOGIN_PARTNERS',
    };

    setLoading(true);

    try {
      const response = await loginWithOTPCode(body);
      await handleAuthResult({ result: response, dispatch, roles, setCollapsed });
    } catch {
      notification.open({
        message: t('OTP_PASSWORD_ERROR'),
        type: 'error',
      });
      setLoading(false);
    }
  };

  const startCountdown = () => {
    setIsDisabled(true);
    setCountdown(60);

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleRecover = async () => {
    if (Window()) {
      startCountdown();
      try {
        const res = await getGoogleValidAccount(user.email);
        if (!res.data) {
          notification.open({
            message: t('ERROR_GOOGLE_ACCOUNT'),
            type: 'error',
          });
        } else {
          auth0Client().changePassword(
            {
              connection: 'Username-Password-Authentication',
              email: user.email,
            },
            (err) => {
              if (err) {
                notification.open({
                  message: t('RECOVER_PASSWORD_ERROR'),
                  type: 'error',
                });
              } else {
                notification.open({
                  message: t('RECOVER_PASSWORD_SUCCESS'),
                  type: 'success',
                });
              }
            }
          );
        }
      } catch {
        notification.open({
          message: t('RECOVER_PASSWORD_ERROR'),
          type: 'error',
        });
      }
    }
  };

  const register = () => {
    if (Window()) {
      setLoading(true);
      dispatch(setLoginLoading(true));
      auth0Client().redirect.signupAndLogin(
        {
          connection: 'Username-Password-Authentication',
          username: user?.email,
          email: user?.email,
          password: user?.password,
          given_name: user?.name,
          family_name: user?.lastName,
        },
        () => {
          setLoading(false);
          dispatch(setLoginLoading(false));
          notification.open({
            message: t('REGISTER_ERROR'),
            type: 'error',
            description: t('REGISTER_ERROR_TEXT'),
          });
        }
      );
    }
  };

  const {
    showModal,
    setShowModal,
    termsAndConditionsLoading,
    termsAndConditionsData,
    showTermsAndConditionsModal,
    closeModal,
  } = useTermsAndConditions({ platform: PARTNERS });

  const resetForm = () => {
    setUser({
      email: '',
      password: '',
      name: '',
      lastName: '',
    });
  };

  return haveAccount ? (
    recoverPassword ? (
      <form
        onSubmit={(e) => e.preventDefault()}
        className="d-flex login-form-container mt-2 mx-auto flex-column justify-content-start align-items-center"
      >
        <LoginInput
          placeholder={t('EMAIL_PLACEHOLDER')}
          name="email"
          value={user?.email}
          onChange={onChange}
          error={loginErrors?.email && loginErrors?.email[0]}
          Icon={MailIcon}
        />
        <div className="d-flex justify-content-around w-100">
          <button
            data-testid="RECOVER_PASSWORD_CANCEL"
            onClick={() => {
              resetForm();
              history.push(SIGNIN);
            }}
            type="button"
            className="bg-transparent font-weight-500  text-xs mt-3 mt-md-0 text-blue-principal border-0"
          >
            {t('CANCEL')}
          </button>
          {!isDisabled ? (
            <DebouncedButton
              data-testid="RECOVER_PASSWORD_SEND_EMAIL"
              onClick={handleRecover}
              type="submit"
              disabled={!user?.email || !!loginErrors?.email}
              className={`rounded-0 border-0 text-white text-xs font-weight-bold p-2 login-form-button ${
                !user?.email || !!loginErrors?.email ? 'bg-maya-blue disabled' : 'bg-maya-blue'
              }`}
            >
              {t('RECOVER_PASSWORD_SEND_EMAIL')}
            </DebouncedButton>
          ) : (
            <p className="mt-1 font-weight-600 text-xs text-grey font-semibold dark:text-white">
              {`${t('WAIT_TO_RESEND_EMAIL_VERIFY')} ${countdown}s`}
            </p>
          )}
        </div>
      </form>
    ) : loginWithOTP ? (
      <form
        onSubmit={(e) => e.preventDefault()}
        className="d-flex login-form-container mt-1 mx-auto flex-column justify-content-start align-items-center"
      >
        {sendCode ? (
          <div className="d-flex flex-column align-items-center gap-4">
            <div className="px-2 login-content-margin">
              <h4
                className="mb-3 text-center font-weight-bold text-xxl my-4"
                data-testid="ly-login-title-h4"
              >
                {t('SEND_CODE_TO_MAIL_MESSAGE')}
              </h4>
            </div>
            <OTPInputCodePassword codeOTP={codeOTP} setCodeOTP={setCodeOTP} />

            {loading ? (
              <div className="d-flex justify-content-center">
                <HorizontalLoader className="h-auto my-4" />
              </div>
            ) : (
              <button
                data-testid="RECOVER_PASSWORD_SEND_EMAIL"
                onClick={loginWithCode}
                type="submit"
                disabled={codeOTP.some((codeChar) => codeChar === '')}
                className={`rounded-sm border-0 text-white text-xs font-weight-bold login-form-button w-50 bg-maya-blue ${
                  codeOTP.some((codeChar) => codeChar === '') ? 'disabled' : ''
                }`}
              >
                {t('LOGIN_WITH_CODE_OTP_BUTTON')}
              </button>
            )}
            <button
              data-testid="RECOVER_PASSWORD_CANCEL"
              onClick={() => {
                setSendCode(false);
                setCodeOTP(initialCodeOTP);
              }}
              type="button"
              className="bg-transparent font-weight-500 w-50 text-xs mt-3 mt-md-0 text-blue-principal border-0"
            >
              {t('CANCEL')}
            </button>
          </div>
        ) : (
          <>
            <LoginInput
              placeholder={t('EMAIL_PLACEHOLDER')}
              name="email"
              value={user?.email}
              onChange={onChange}
              error={loginErrors?.email && loginErrors?.email[0]}
              Icon={MailIcon}
            />
            {error && (
              <span className="danger-text  m-0 mt-3" data-testid="login-error-wrong-fields-span">
                ⚠️ {t('WRONG_EMAIL_OR_PASSWORD')}
              </span>
            )}
            <div className="d-flex flex-column mt-4 gap-2 w-100 justify-content-center align-items-center">
              {loading ? (
                <div className="h-100 py-2 mt-2">
                  <HorizontalLoader className="h-100" />
                </div>
              ) : (
                <button
                  data-testid="LOGIN_BUTTON"
                  onClick={sendEmailLoginCode}
                  type="submit"
                  disabled={
                    !user?.email ||
                    (loginErrors?.email && loginErrors?.email[0]) ||
                    error ||
                    isOTPDisabled
                  }
                  className={`rounded-sm border-0 text-white text-xs font-weight-bold w-50 login-form-button ${
                    !user?.email ||
                    (loginErrors?.email && loginErrors?.email[0]) ||
                    error ||
                    isOTPDisabled
                      ? 'bg-maya-blue disabled'
                      : 'bg-maya-blue '
                  }`}
                >
                  {t('LOGIN_WITH_CODE_OTP_MESSAGE')}
                </button>
              )}
              {isOTPDisabled && (
                <p className="mt-1 mb-2 font-weight-600 text-xs text-purple font-semibold text-center">
                  {`${t('WAIT_TO_RESEND_EMAIL_VERIFY')} ${Math.floor(otpCountdown / 60)}:${(
                    otpCountdown % 60
                  )
                    .toString()
                    .padStart(2, '0')}`}
                </p>
              )}
            </div>
            <div className="d-flex mt-4 w-100">
              <button
                data-testid={
                  haveAccount ? 'DONT_HAVE_ACCOUNT_BUTTON' : 'ALREADY_HAVE_ACCOUNT_BUTTON'
                }
                className="text-purple font-weight-600 w-50 border-0 bg-transparent text-underline"
                type="button"
                onClick={() => {
                  history.push(haveAccount ? REGISTER : SIGNIN);
                  setHaveAccount((state) => !state);
                }}
              >
                {haveAccount ? t('CREATION_USER_BUTTON') : t('LOGIN_USER_BUTTON')}
              </button>
              <button
                data-testid="LOGIN_BUTTON"
                onClick={() => {
                  setLoginWithOTP(false);
                }}
                type="button"
                className="text-purple font-weight-600 w-50 border-0 bg-transparent text-underline"
              >
                {t('LOGIN_IN_WITH_PASSWORD')}
              </button>
            </div>
          </>
        )}
      </form>
    ) : (
      <form
        onSubmit={(e) => e.preventDefault()}
        className="d-flex login-form-container mt-1 mx-auto flex-column justify-content-start align-items-center"
      >
        <LoginInput
          placeholder={t('EMAIL_PLACEHOLDER')}
          name="email"
          value={user?.email}
          onChange={onChange}
          error={loginErrors?.email && loginErrors?.email[0]}
          Icon={MailIcon}
        />
        <LoginInput
          placeholder={t('PASSWORD_PLACEHOLDER')}
          type="password"
          name="password"
          value={user?.password}
          Icon={PasswordIcon}
          onChange={onChange}
          isPassword
        />
        <div className="w-100">
          <button
            data-testid="FORGOT_MY_PASSWORD_BUTTON"
            onClick={() => {
              history.push(FORGOTPASSWORD);
              resetForm();
            }}
            type="button"
            className="bg-transparent font-weight-500  text-xs text-blue-principal border-0"
          >
            {t('FORGOT_MY_PASSWORD')}
          </button>
        </div>
        {error && (
          <span className="danger-text  m-0 mt-3" data-testid="login-error-wrong-fields-span">
            ⚠️ {t('WRONG_EMAIL_OR_PASSWORD')}
          </span>
        )}
        <div className="d-flex mt-4 w-100 justify-content-center">
          {loading ? (
            <div className="h-100 py-2 mt-2">
              <HorizontalLoader className="h-100" />
            </div>
          ) : (
            <button
              data-testid="LOGIN_BUTTON"
              onClick={loginPassword}
              type="submit"
              disabled={
                !user?.email ||
                !user?.password ||
                (loginErrors?.email && loginErrors?.email[0]) ||
                error
              }
              className={`rounded-sm border-0 text-white text-xs font-weight-bold w-50 login-form-button ${
                !user?.email ||
                !user?.password ||
                (loginErrors?.email && loginErrors?.email[0]) ||
                error
                  ? 'bg-maya-blue disabled'
                  : 'bg-maya-blue '
              }`}
            >
              {t('LOGIN_IN_TITLE')}
            </button>
          )}
        </div>
        <div className="d-flex mt-4 w-100">
          <button
            data-testid={haveAccount ? 'DONT_HAVE_ACCOUNT_BUTTON' : 'ALREADY_HAVE_ACCOUNT_BUTTON'}
            className="text-purple font-weight-600 w-50 border-0 bg-transparent text-underline"
            type="button"
            onClick={() => {
              history.push(haveAccount ? REGISTER : SIGNIN);
              setHaveAccount((state) => !state);
            }}
          >
            {haveAccount ? t('CREATION_USER_BUTTON') : t('LOGIN_USER_BUTTON')}
          </button>
          <button
            data-testid="LOGIN_BUTTON"
            onClick={() => {
              setLoginWithOTP(true);
            }}
            type="button"
            className="text-purple font-weight-600 w-50 border-0 bg-transparent text-underline"
          >
            {t('LOGIN_IN_WITH_CODE')}
          </button>
        </div>
      </form>
    )
  ) : (
    <>
      <Modal
        className="h-100 pt-3 pb-2 px-4 modal-tac-container b-radius-8"
        show={showModal}
        setShow={setShowModal}
        closeBtnClassname="text-blue-principal modal-close-icon"
      >
        <>
          {termsAndConditionsLoading ? (
            <Spinner show />
          ) : termsAndConditionsData ? (
            <div className="d-flex flex-column h-100 py-4">
              <span
                className=" font-weight-600 text-xs mb-2"
                data-testid="terms-and-conditions-title"
              >
                {t('TERMS_AND_CONDITIONS_MODAL_TITLE')}
              </span>
              <PDFViewer pdf={termsAndConditionsData?.link} />
              <button
                type="button"
                onClick={closeModal}
                className="rounded-pill border-0 text-white px-4 text-xs font-weight-500  login-form-button bg-maya-blue w-fit close-modal-button mt-3"
                data-testid="terms-and-conditions-back-btn"
              >
                {t('TERMS_AND_CONDITIONS_GO_BACK')}
              </button>
            </div>
          ) : (
            <span data-testid="terms-and-conditions-cannot-loading-span">
              {t('CANNOT_LOAD_TERMS_AND_CONDITIONS')}
            </span>
          )}
        </>
      </Modal>
      <form
        onSubmit={(e) => e.preventDefault()}
        className="d-flex login-form-container mt-2 mx-auto flex-column justify-content-start align-items-center "
      >
        <LoginInput
          placeholder={t('PROFILE_NAME')}
          value={user?.name}
          name="name"
          onChange={onChange}
          error={loginErrors?.name && loginErrors?.name[0]}
          Icon={UserIcon}
        />
        <LoginInput
          placeholder={t('PROFILE_LASTNAME')}
          value={user?.lastName}
          name="lastName"
          onChange={onChange}
          error={loginErrors?.lastName && loginErrors?.lastName[0]}
          Icon={UserIcon}
        />
        <LoginInput
          placeholder={t('EMAIL_PLACEHOLDER')}
          value={user?.email}
          name="email"
          onChange={onChange}
          error={loginErrors?.email && loginErrors?.email[0]}
          Icon={MailIcon}
        />
        <LoginInput
          placeholder={t('PASSWORD_PLACEHOLDER')}
          name="password"
          value={user?.password}
          type="password"
          error={
            (loginErrors?.password &&
              loginErrors?.password?.length > 1 &&
              loginErrors?.password[0]) ||
            (Boolean(user?.password?.length && user?.password?.length < 8) &&
              t('PASSWORD_VALIDATION_LENGTH'))
          }
          onChange={onChange}
          Icon={PasswordIcon}
          isPassword
        />
        <div className="d-flex mt-1 w-100 p-0">
          <span className="ml-2  text-xs text-gray">
            {t('ACCEPTED_TERMS_AND_CONDITIONS')}
            <button
              data-testid="TERMS_AND_CONDITIONS_BUTTON"
              type="button"
              onClick={showTermsAndConditionsModal}
              className="border-0 cursor-pointer text-purple bg-transparent"
            >
              {t('TERMS_AND_CONDITIONS')}
            </button>
          </span>
        </div>
        <div className="d-flex mt-3 w-100">
          <button
            data-testid={haveAccount ? 'CREATION_USER_BUTTON' : 'LOGIN_USER_BUTTON'}
            className="text-purple font-weight-600 w-50 border-0 bg-transparent text-underline"
            type="button"
            onClick={() => {
              history.push(haveAccount ? REGISTER : SIGNIN);
              setHaveAccount((state) => !state);
            }}
          >
            {haveAccount ? t('CREATION_USER_BUTTON') : t('LOGIN_USER_BUTTON')}
          </button>
          {loading ? (
            <div className="d-flex justify-content-center w-50">
              <HorizontalLoader className="h-auto my-2" />
            </div>
          ) : (
            <button
              data-testid="REGISTER_BUTTON"
              onClick={register}
              type="submit"
              disabled={
                !user?.email ||
                !user?.password ||
                !user?.name ||
                !user.lastName ||
                !!loginErrors?.email ||
                loginErrors?.password?.length > 1 ||
                user?.password?.length < 8 ||
                !!loginErrors?.name ||
                !!loginErrors?.lastName
              }
              className={`rounded-sm border-0 text-white text-xs font-weight-bold w-50 login-form-button ${
                !user?.email ||
                !user?.password ||
                !user?.name ||
                !user.lastName ||
                !!loginErrors?.email ||
                loginErrors?.password?.length > 1 ||
                user?.password?.length < 8 ||
                !!loginErrors?.name ||
                !!loginErrors?.lastName
                  ? 'bg-maya-blue disabled'
                  : 'bg-maya-blue'
              }`}
            >
              {t('CREATE_ACCOUNT')}
            </button>
          )}
        </div>
      </form>
    </>
  );
};

export default LoginForm;
